<template>
  <div class="graph-editor">
    <!-- Library Panel (nur Edit Mode) -->
    <div v-if="isEditMode && showLibrary" class="library-panel" :style="{ width: libraryWidth + 'px' }">
      <div class="panel-header">
        <h3>Library</h3>
        <button @click="showLibrary = false" class="close-btn">×</button>
      </div>
      <div class="template-grid">
        <div 
          v-for="template in templates" 
          :key="template.id"
          class="template-item"
          :style="{ backgroundColor: template.color }"
          draggable="true"
          @dragstart="onTemplateDragStart($event, template)"
        >
          <div class="template-shape" :class="template.shape">{{ template.name }}</div>
        </div>
      </div>
      <div class="resize-handle-right" @mousedown="startResize('library', $event)"></div>
    </div>

    <!-- Graph Panel -->
    <div class="graph-panel" ref="graphContainer">
      <div class="toolbar">
        <div class="toolbar-left">
          <button v-if="isEditMode && !showLibrary" @click="showLibrary = true" class="btn">📚 Vorlagen</button>
          <button @click="zoomToFit" class="btn">🎯 Fit</button>
          <button @click="setZoom(1)" :class="{ active: zoomLevel === 1 }" class="btn">100%</button>
          <button @click="setZoom(0.5)" :class="{ active: zoomLevel === 0.5 }" class="btn">50%</button>
          <div class="divider"></div>

          <!-- Link-Button -->
          <button 
            v-if="isEditMode" 
            @click="toggleLinkMode" 
            :class="{ active: linkMode }" 
            class="btn link-btn"
            :title="linkMode ? 'Link-Modus beenden' : 'Verbindung erstellen'"
          >
            🔗 {{ linkMode ? 'Abbrechen' : 'Verbindung' }}
          </button>
          <div class="divider"></div>
          <button v-if="isEditMode" @click="undo" :disabled="!canUndo" class="btn">↶</button>
          <button v-if="isEditMode" @click="redo" :disabled="!canRedo" class="btn">↷</button>
          <button
            v-if="isEditMode && multiSelectedCount"
            @click="deleteMultiSelection"
            class="btn btn-danger"
            title="Ausgewählte Elemente löschen (Entf)"
          >
            🗑️ {{ multiSelectedCount }} löschen
          </button>
        </div>
        <div class="toolbar-right">
          <button @click="exportGraph" class="btn">💾 Export</button>
          <input type="file" ref="fileInput" @change="importGraph" accept=".json" style="display: none">
          <button @click="$refs.fileInput.click()" class="btn">📁 Import</button>
        </div>
      </div>
      <div ref="paperContainer" class="paper-container" :class="{ 'link-mode': linkMode }"></div>
    </div>

    <!-- Property Panel -->
    <div v-if="showProperties" class="property-panel" :style="{ width: propertyWidth + 'px' }">
      <div class="panel-header">
        <h3>{{ selectedElement ? 'Eigenschaften' : 'Kein Element gewählt' }}</h3>
        <button @click="closeProperties" class="close-btn">×</button>
      </div>

      <div v-if="selectedElement" class="property-content">
        <!-- Edit Mode Properties -->
        <div v-if="isEditMode" class="edit-properties">
            <div class="form-group">
                <label>Titel:</label>
                <input v-model="selectedElement.title" @input="updateElement" class="form-input">
            </div>
          
          <!-- div v-if="selectedElement.type === 'node'" class="form-group">
            <label>Form:</label>
            <select v-model="selectedElement.shape" @change="updateElement" class="form-select">
              <option value="rectangle">Rechteck</option>
              <option value="ellipse">Oval</option>
            </select>
          </div -->

          <!-- <div class="form-group">
            <label>Farbe:</label>
            <input v-model="selectedElement.color" @input="updateElement" type="color" class="form-color">
          </div> -->

            <div v-if="selectedElement.type === 'node'" class="form-group">
                <label>Größe:</label>
                <select v-model="selectedElement.size" @change="updateElement" class="form-select">
                    <option value="small">Klein</option>
                    <option value="large">Groß</option>
                </select>
            </div>
            <div v-if="selectedElement.type === 'link'" class="form-group">
                <label>Dicke:</label>
                <select v-model="selectedElement.thickness" @change="updateElement" class="form-select">
                    <option value="thin">Dünn</option>
                    <option value="thick">Dick</option>
                </select>
            </div>
            <div v-if="selectedElement.type === 'link'" class="form-group checkbox-group">
                <label>Kritische Verbindung:
                    <input v-model="selectedElement.critical" @change="updateElement" class="form-input" type="checkbox" />
                </label>
            </div>
            <div class="form-group">
                <label>Markdown:</label>
                <div ref="markdownEditor" class="markdown-editor"></div>
            </div>

            <!-- Fields for date / duration and for costs -->

            <div v-if="selectedElement.nodeType === 'start'" class="form-group">
                <label>Start</label>
                <!-- https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/input/date -->
                <input v-model="selectedElement.start" @input="updateElement" class="form-input" type="date" />
            </div>

            <div v-if="selectedElement.type === 'link'" class="form-group">
                <label>Zeitaufwand: (Wochen)</label>
                <input v-model="selectedElement.duration" @input="updateElement" class="form-input" type="number" />
            </div>

            <div v-if="selectedElement.type === 'link'" class="form-group">
                <label>Kosten (€):</label>
                <input v-model="selectedElement.costs" @input="updateElement" class="form-input">
            </div>
        </div>

        <!-- View Mode Properties -->
        <div v-else class="view-properties">
          <div class="markdown-preview" v-html="renderedMarkdown"></div>
          <div v-if="Number.isFinite(selectedElement.costs)" class="view-single-property">Kosten (€): {{ selectedElement.costs }}</div>
          <div v-if="Number.isFinite(selectedElement.duration)" class="view-single-property">Zeitaufwand (Wochen): {{ selectedElement.duration }}</div>
        </div>
      </div>

      <div class="resize-handle-left" @mousedown="startResize('property', $event)"></div>
    </div>

    <!-- Kontextmenü -->
    <div 
      v-if="contextMenu.visible" 
      class="context-menu"
      :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }"
      @click.stop
    >
      <button @click="startLinkFromElement(contextMenu.element)" class="context-item">
        🔗 Verbindung erstellen
      </button>
      <button @click="deleteElement(contextMenu.element)" class="context-item">
        🗑️ Element löschen
      </button>
    </div>

    <!-- Mode Toggle -->
    <div class="mode-toggle">
      <button @click="toggleMode" class="mode-btn" :class="{ locked: !isEditMode }">
        {{ isEditMode ? '🔓' : '🔒' }}
      </button>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import * as joint from '@joint/core'
import { EditorView, basicSetup } from 'codemirror'
import { markdown } from '@codemirror/lang-markdown'
import { oneDark } from '@codemirror/theme-one-dark'
import { marked } from 'marked'
import { hyphenateSync } from 'hyphen/de'
import templates from '../data/templates.json'
import exampleGraph from '../data/example_graph.json'

const { dia, shapes, V } = joint;

const SIZES_FOR_FORM = {
    ellipse: {
        small: { width: 110, height: 70 },
        large: { width: 180, height: 105 }
    },
    circle: {
        small: { width: 105, height: 105 },
        large: { width: 140, height: 140 }
    },
    rectangle: {
        small: { width: 110, height: 70 },
        large: { width: 180, height: 105 }
    }
};

const warnColor = '#bbaa2b';

const LABEL_FONT_SIZE = 12;
// Maximale Zeilenbreite in px, ab der der Titel umbrochen wird
const MAX_LABEL_LINE_WIDTH = 130;
// Anteil der Knotenbreite/-höhe, den der Text in runden Formen nutzen darf
// (einbeschriebenes Rechteck einer Ellipse: 1/sqrt(2) ≈ 0.7)
const ELLIPSE_TEXT_RATIO = 0.7;
const RECT_TEXT_PADDING = 10;

let measureSvg = null;

/**
 * Misst die Pixelgröße eines (ggf. mehrzeiligen) Label-Textes über ein
 * unsichtbares SVG im Dokument, damit die Schrift der gerenderten entspricht.
 */
function measureLabelText(text) {
    if (!measureSvg) {
        measureSvg = V('svg', { width: 1, height: 1 });
        measureSvg.node.style.cssText = 'position:absolute;left:-10000px;top:-10000px;visibility:hidden';
        document.body.appendChild(measureSvg.node);
    }
    const vText = V('text', { 'font-size': LABEL_FONT_SIZE });
    vText.text(String(text));
    measureSvg.append(vText);
    const bbox = vText.node.getBBox();
    vText.remove();
    return bbox;
}

const SOFT_HYPHEN = '­';

/**
 * Bricht einen Titel in Zeilen von maximal MAX_LABEL_LINE_WIDTH px um.
 * Zu lange Wörter werden nach den deutschen Regeln zur Worttrennung an
 * Silbengrenzen getrennt und erhalten einen sichtbaren Bindestrich;
 * vorhandene Bindestriche gelten als Trennstellen (ohne Verdopplung).
 */
function wrapLabelText(title) {
    const fits = (line) => measureLabelText(line).width <= MAX_LABEL_LINE_WIDTH;
    const withHyphen = (part) => part.endsWith('-') ? part : part + '-';

    const lines = [];
    let line = '';

    String(title).trim().split(/\s+/).forEach((word) => {
        if (!word) {
            return;
        }
        const appended = line ? line + ' ' + word : word;
        if (fits(appended)) {
            line = appended;
            return;
        }
        if (line) {
            lines.push(line);
            line = '';
        }
        if (fits(word)) {
            line = word;
            return;
        }

        // Wort ist länger als eine Zeile: bevorzugt an vorhandenen
        // Bindestrichen trennen, sonst an Silbengrenzen
        const chunks = word.split(/(?<=-)/).filter(Boolean);
        let acc = '';
        chunks.forEach((chunk, cIdx) => {
            const chunkIsLast = cIdx === chunks.length - 1;
            const appendedChunk = acc + chunk;
            if (fits(chunkIsLast ? appendedChunk : withHyphen(appendedChunk))) {
                acc = appendedChunk;
                return;
            }
            if (acc && fits(chunkIsLast ? chunk : withHyphen(chunk))) {
                // Chunk passt allein in eine Zeile: am Bindestrich trennen
                lines.push(withHyphen(acc));
                acc = chunk;
                return;
            }
            // Chunk selbst zu lang: an Silbengrenzen trennen
            const syllables = hyphenateSync(chunk).split(SOFT_HYPHEN).filter(Boolean);
            syllables.forEach((syllable, sIdx) => {
                const isLast = chunkIsLast && sIdx === syllables.length - 1;
                const joined = acc + syllable;
                if (!acc || fits(isLast ? joined : withHyphen(joined))) {
                    acc = joined;
                } else {
                    lines.push(withHyphen(acc));
                    acc = syllable;
                }
            });
        });
        line = acc;
    });

    if (line) {
        lines.push(line);
    }
    return lines.join('\n');
}

/**
 * Ermittelt Knotengröße und umbrochenen Label-Text so, dass der Titel
 * vollständig hineinpasst. Die Standardgröße der Form ist die Mindestgröße.
 *
 * Might return `undefined` (bei unbekannter Form/Größe).
 * @param template Objekt mit shape, size und title
 * @returns {{width: number, height: number, text: string}|undefined}
 */
function getLabelFit(template) {
    const base = getDefaultSizeOfShape(template);
    if (!base) {
        return;
    }

    const title = String(template.title || '').trim();
    if (!title) {
        return { ...base, text: '' };
    }

    const wrapped = wrapLabelText(title);
    const bbox = measureLabelText(wrapped);
    const textWidth = bbox.width + 4;
    const textHeight = bbox.height + 4;

    let width, height;
    switch (template.shape) {
        case 'circle': {
            // Die Textbox passt in den Kreis, wenn ihre Diagonale <= Durchmesser ist
            const diameter = Math.ceil(Math.hypot(textWidth, textHeight));
            width = height = Math.max(base.width, diameter);
            break;
        }
        case 'ellipse':
            width = Math.max(base.width, Math.ceil(textWidth / ELLIPSE_TEXT_RATIO));
            height = Math.max(base.height, Math.ceil(textHeight / ELLIPSE_TEXT_RATIO));
            break;
        default:
            width = Math.max(base.width, Math.ceil(textWidth) + 2 * RECT_TEXT_PADDING);
            height = Math.max(base.height, Math.ceil(textHeight) + 2 * RECT_TEXT_PADDING);
    }
    return { width, height, text: wrapped };
}

/**
 * Passt Größe und Label-Text aller Knoten in einem Graph-JSON an die Titel
 * an — VOR dem Rendern via graph.fromJSON. Dadurch wird der Graph in einem
 * Zug mit finaler Geometrie gezeichnet und die Kanten werden korrekt
 * geroutet (nachträgliche resize()-Aufrufe hinterließen im Browser teils
 * falsch platzierte Kanten). Mutiert und liefert das übergebene JSON.
 */
function fitGraphJSONToLabels(graphJSON) {
    if (!graphJSON || !Array.isArray(graphJSON.cells)) {
        return graphJSON;
    }
    graphJSON.cells.forEach((cell) => {
        const data = cell.data || {};
        if ((data.type && data.type !== 'node') || !cell.type || cell.type === 'standard.Link') {
            return;
        }
        const shape = data.shape || 'rectangle';
        const label = (cell.attrs && cell.attrs.label) || {};
        // Fallback für Knoten ohne data.title: bereits umbrochenen
        // Label-Text wieder zu einer Zeile zusammensetzen
        const title = data.title
            ?? String(label.text || '').replace(/-\n/g, '').replace(/\n/g, ' ');
        const fit = getLabelFit({ shape, size: data.size || 'small', title });
        if (!fit) {
            return;
        }
        cell.size = { width: fit.width, height: fit.height };
        if (cell.attrs && cell.attrs.label) {
            cell.attrs.label.text = fit.text;
            delete cell.attrs.label.textWrap;
        }
    });
    return graphJSON;
}

/**
 * Tries to find right size object for template, dependent on shape and size (sm/lg).
 * 
 * Might return `undefined`.
 * @param template of node
 */
function getDefaultSizeOfShape(template) {
    if ((template === null) || (typeof template !== 'object')) {
        console.log('\x1b[31m PROBLEM \x1b[0m', 'Weird input (no object) for getDefaultSizeOfShape. Need template', template);
        return;
    }
    if (!template.shape || !template.size) {
        console.log('\x1b[31m PROBLEM \x1b[0m', 'Unsupported input type in getDefaultSizeOfShape, but is object', template);
        return;
    }
    if (SIZES_FOR_FORM[template.shape] && SIZES_FOR_FORM[template.shape][template.size]) {
        return SIZES_FOR_FORM[template.shape][template.size];
    }
    console.log('\x1b[31m PROBLEM \x1b[0m', 'Template shape/size is not supported', template);
}

export default {
  name: 'GraphEditor',
  props: {
    // Ob der Editor gerade sichtbar ist (v-show in App.vue). Der Graph darf
    // erst bei Sichtbarkeit gerendert werden, sonst stimmen die Kanten nicht.
    visible: {
      type: Boolean,
      default: true
    }
  },
  setup(props) {
    // Refs
    const graphContainer = ref(null);
    const paperContainer = ref(null);
    const markdownEditor = ref(null);
    const fileInput = ref(null);

    // Reactive state
    const isEditMode = ref(true);
    const showLibrary = ref(true);
    const showProperties = ref(false);
    const zoomLevel = ref(1);
    const libraryWidth = ref(250);
    const propertyWidth = ref(300);

    // Link-spezifische States
    const linkMode = ref(false);
    const contextMenu = reactive({
      visible: false,
      x: 0,
      y: 0,
      element: null
    });
    let linkStart = null;

    // Graph state
    let graph = null;
    let paper = null;
    let editorView = null;

    const selectedElement = ref(null);

    // Mehrfachselektion (Rubber-Band bzw. Shift/Strg-Klick)
    const multiSelected = ref([]);
    const multiSelectedCount = computed(() => multiSelected.value.length);

    const history = reactive([]);
    const historyIndex = ref(-1);
    const canUndo = ref(false);
    const canRedo = ref(false);

    // Auto-save
    let autoSaveTimer = null;
    const hasUnsavedChanges = ref(false);

    // Initialize graph
    const initGraph = () => {
        graph = new dia.Graph({}, { cellNamespace: joint.shapes });

        paper = new dia.Paper({
            el: paperContainer.value,
            model: graph,
            width: '100%',
            height: '100%',
            gridSize: 10,
            drawGrid: true,
            background: { color: '#ffffff' },
            interactive: isEditMode.value,
            cellNamespace: joint.shapes,
            linkPinning: false,
            snapLinks: true,
            // marks nodes on drawing an edge
            markAvailable: true,
            
            validateConnection: function(cellViewS, magnetS, cellViewT, magnetT, end, linkView) {
            if (cellViewS === cellViewT) {
                return false;
            }
            return cellViewS.model.isElement() && cellViewT.model.isElement();
            },

            defaultLink: () => new shapes.standard.Link({
            attrs: {
                line: {
                stroke: '#3498db',
                strokeWidth: 2,
                targetMarker: { 
                    type: 'path', 
                    d: 'M 10 -5 0 0 10 5 z',
                    fill: '#3498db'
                }
                }
            }
            })
        });

        // Event listeners
        paper.on('element:pointerclick', onElementClick);
        paper.on('link:pointerclick', onLinkClick);
        paper.on('element:contextmenu', onElementContextMenu);
        paper.on('link:contextmenu', onElementContextMenu);
        paper.on('blank:pointerclick', onBlankClick);
        paper.on('blank:pointerdown', onBlankPointerDown);
        paper.on('link:connect', onLinkConnect);

        // Entfernte Zellen aus der Mehrfachauswahl nehmen
        graph.on('remove', (cell) => {
            if (multiSelected.value.includes(cell)) {
                multiSelected.value = multiSelected.value.filter((c) => c !== cell);
            }
        });

        let historyTimeout = null;

        // History tracking
        graph.on('all', () => {
            if (isEditMode.value && graph) {
                hasUnsavedChanges.value = true;
                if (historyTimeout) {
                    clearTimeout(historyTimeout);
                }
                historyTimeout = setTimeout(() => {
                    saveToHistory()
                    scheduleAutoSave()
                }, 500)
            }
        })

        // Drag and drop from library
        paperContainer.value.addEventListener('dragover', e => { e.preventDefault(); })
        paperContainer.value.addEventListener('drop', onDrop)
    };

    // Template drag start
    const onTemplateDragStart = (event, template) => {
        // what the hell is this ??? "transfer" to what?
        event.dataTransfer.setData('application/json', JSON.stringify(template))
    };

    // Drop handler
    const onDrop = (event) => {
        event.preventDefault();
        if (!isEditMode.value) {
            return;
        }

        try {
            const template = JSON.parse(event.dataTransfer.getData('application/json'));
            const rect = paperContainer.value.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;

            createNodeFromTemplate(template, x, y);
        } catch (e) {
            console.error('Drop error:', e)
        };
    };

    // Create node from template
    const createNodeFromTemplate = (template, x, y) => {
        const fit = getLabelFit({ ...template, title: template.name });
        let element;

        if (fit) {
            const size = { width: fit.width, height: fit.height };
            const position = { x: x - size.width/2, y: y - size.height/2 };
            const sdtAttr = {
                body: {
                    fill: template.color,
                    stroke: '#333',
                    strokeWidth: 1,
                    magnet: true
                },
                label: {
                    text: fit.text,
                    fontSize: LABEL_FONT_SIZE,
                    fill: '#333'
                }
            };

            switch (template.shape) {
                case 'ellipse':
                    element = new shapes.standard.Ellipse({
                        position,
                        size,
                        attrs: sdtAttr
                    });
                break;
                case 'rectangle': 
                    element = new shapes.standard.Rectangle({
                        position,
                        size,
                        attrs: sdtAttr
                    });
                    break;
                case 'circle':
                    element = new shapes.standard.Circle({
                        position,
                        size,
                        attrs: sdtAttr
                    });
                    break;
                default:
                    console.log('\x1b[31m PROBLEM \x1b[0m', 'Unsupported template shape', template);
            }

            if (element) {
                element.attr('label/x', 'calc(0.5*w)');
                element.attr('label/y', 'calc(0.5*h)');
                element.set('data', {
                    title: template.name,
                    nodeType: template.id,
                    shape: template.shape,
                    color: template.color,
                    size: template.size,
                    markdown: `# ${template.name}\n\nBeschreibung hier...`,
                    type: 'node'
                });

                graph.addCell(element);
            } else {
                console.log('\x1b[31m PROBLEM \x1b[0m', 'Unsupported template, NOT ADDED !!!', template);
            }
        }
    }

    // ----- Mehrfachselektion -----
    const MULTI_SELECT_HIGHLIGHT = 'multi-selection';

    const setCellHighlight = (cell, on) => {
        const view = cell.findView(paper);
        if (!view) {
            return;
        }
        if (on) {
            joint.highlighters.mask.add(view, { selector: cell.isLink() ? 'line' : 'body' }, MULTI_SELECT_HIGHLIGHT, {
                padding: 4,
                attrs: { stroke: '#0d6efd', 'stroke-width': 2, 'stroke-linejoin': 'round' }
            });
        } else {
            joint.highlighters.mask.remove(view, MULTI_SELECT_HIGHLIGHT);
        }
    };

    const clearMultiSelection = () => {
        multiSelected.value.forEach((cell) => setCellHighlight(cell, false));
        multiSelected.value = [];
    };

    const addToMultiSelection = (cells) => {
        const added = cells.filter((cell) => !multiSelected.value.includes(cell));
        added.forEach((cell) => setCellHighlight(cell, true));
        multiSelected.value = [...multiSelected.value, ...added];
    };

    const toggleMultiSelect = (cell) => {
        if (multiSelected.value.includes(cell)) {
            setCellHighlight(cell, false);
            multiSelected.value = multiSelected.value.filter((c) => c !== cell);
        } else {
            addToMultiSelection([cell]);
        }
        // Einzelselektion (Eigenschaften-Panel) schließt sich bei Mehrfachauswahl
        deselectElement();
    };

    const deleteMultiSelection = () => {
        const cells = [...multiSelected.value];
        clearMultiSelection();
        graph.removeCells(cells);
        contextMenu.visible = false;
        deselectElement();
    };

    // Rubber-Band: Aufziehen eines Auswahlrechtecks auf freier Fläche
    const onBlankPointerDown = (evt, x, y) => {
        if (!isEditMode.value || linkMode.value) {
            return;
        }
        const container = paperContainer.value;
        const start = { clientX: evt.clientX, clientY: evt.clientY, x, y };
        let lassoEl = null;

        const onMove = (e) => {
            const dx = e.clientX - start.clientX;
            const dy = e.clientY - start.clientY;
            if (!lassoEl) {
                if (Math.abs(dx) < 5 && Math.abs(dy) < 5) {
                    return; // noch als Klick werten
                }
                lassoEl = document.createElement('div');
                lassoEl.className = 'selection-lasso';
                container.appendChild(lassoEl);
            }
            const rect = container.getBoundingClientRect();
            lassoEl.style.left = (Math.min(e.clientX, start.clientX) - rect.left) + 'px';
            lassoEl.style.top = (Math.min(e.clientY, start.clientY) - rect.top) + 'px';
            lassoEl.style.width = Math.abs(dx) + 'px';
            lassoEl.style.height = Math.abs(dy) + 'px';
        };

        const onUp = (e) => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            if (!lassoEl) {
                return; // war nur ein Klick -> onBlankClick übernimmt
            }
            lassoEl.remove();

            const end = paper.clientToLocalPoint({ x: e.clientX, y: e.clientY });
            const area = new joint.g.Rect(
                Math.min(start.x, end.x),
                Math.min(start.y, end.y),
                Math.abs(end.x - start.x),
                Math.abs(end.y - start.y)
            );
            // Knoten im Auswahlbereich; Kanten kommen mit, wenn beide
            // Endpunkte ausgewählt sind (einzelne Kanten: Strg/Shift-Klick)
            const elements = graph.findModelsInArea(area);
            const links = graph.getLinks().filter((link) => {
                const src = link.getSourceElement();
                const tgt = link.getTargetElement();
                return src && tgt && elements.includes(src) && elements.includes(tgt);
            });

            if (!(e.ctrlKey || e.metaKey || e.shiftKey)) {
                clearMultiSelection();
            }
            addToMultiSelection([...elements, ...links]);
            deselectElement();
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    // Entf/Backspace löscht die aktuelle Mehrfachauswahl
    const onKeyDown = (e) => {
        if (!isEditMode.value || (e.key !== 'Delete' && e.key !== 'Backspace')) {
            return;
        }
        const t = e.target;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
            return; // Eingabefelder (inkl. Markdown-Editor) nicht stören
        }
        if (multiSelected.value.length) {
            e.preventDefault();
            deleteMultiSelection();
        }
    };

    // Element selection
    const onElementClick = (elementView, evt) => {
      if (linkMode.value) {
        handleLinkModeClick(elementView);
      } else if (isEditMode.value && evt && (evt.ctrlKey || evt.metaKey || evt.shiftKey)) {
        toggleMultiSelect(elementView.model);
      } else {
        clearMultiSelection();
        selectElement(elementView.model, 'node');
      }
    }

    // Link selection
    const onLinkClick = (linkView, evt) => {
        if (linkMode.value) {
            return;
        }
        if (isEditMode.value && evt && (evt.ctrlKey || evt.metaKey || evt.shiftKey)) {
            toggleMultiSelect(linkView.model);
        } else {
            clearMultiSelection();
            selectElement(linkView.model, 'link');
        }
    }

    const onBlankClick = () => {
      contextMenu.visible = false;
      if (linkMode.value) {
        cancelLinkMode();
      }
      clearMultiSelection();
      deselectElement();
    }

    const onElementContextMenu = (elementView, event) => {
        // console.log('\x1b[32m DEBUG dev \x1b[0m','on element context menu', 'return', 'eventX', event.clientX, 'eventY', event.clientY);
        if (!isEditMode.value) {
            event.preventDefault();
            return;
        }

        event.preventDefault();
        contextMenu.visible = true;
        contextMenu.x = event.clientX;
        contextMenu.y = event.clientY;
        contextMenu.element = elementView.model;
    }

    const onLinkConnect = (linkView) => {
        const link = linkView.model;
        if (!link.get('data')) {
            link.set('data', {
                title: 'Neue Verbindung',
                color: '#3498db',
                thickness: 'thin',
                critical: false,
                markdown: '# Verbindung\n\nBeschreibung der Beziehung...',
                type: 'link'
            });
        }
        link.labels(['Neue Verbindung']);
    }

    // Link-spezifische Funktionen
    const handleLinkModeClick = (elementView) => {
        // console.log('\x1b[32m DEBUG dev \x1b[0m', 'what is linkStart', linkStart);
        if (!linkStart) {
            linkStart = elementView.model;
            elementView.highlight();
            console.log('\x1b[32m DEBUG dev \x1b[0m', 'set to model', linkStart, 'highlighted');
        } else if (linkStart !== elementView.model) {
            createLink(linkStart, elementView.model);
            linkStart.unhighlight();
            cancelLinkMode();
            console.log('\x1b[32m DEBUG dev \x1b[0m', 'link created');
        } else {
            console.log('\x1b[32m DEBUG dev \x1b[0m', 'handle link mode ....');
        }
    }

    const createLink = (source, target) => {
        const link = new shapes.standard.Link({
            source: { id: source.id },
            target: { id: target.id },
            attrs: {
            line: {
                stroke: '#3498db',
                strokeWidth: 2,
                targetMarker: { 
                    type: 'path', 
                    d: 'M 10 -5 0 0 10 5 z',
                    fill: '#3498db'
                    }
                }
            }
        });

        // link data of course do not have nodeType
        link.set('data', {
            title: 'Neue Verbindung',
            color: '#3498db',
            thickness: 'thin',
            critical: false,
            markdown: '# Verbindung\n\nBeschreibung der Beziehung...',
            type: 'link'
        });

        link.labels(['Neue Verbindung']);

        graph.addCell(link);
    }

    const toggleLinkMode = () => {
        linkMode.value = !linkMode.value;
        if (!linkMode.value) {
            cancelLinkMode();
        } else {
            paperContainer.value.style.cursor = 'crosshair';
        }
    }

    const cancelLinkMode = () => {
        linkMode.value = false;
        if (linkStart) {
            linkStart.unhighlight();
            linkStart = null;
        }
        paperContainer.value.style.cursor = '';
    }

    const startLinkFromElement = (element) => {
        linkStart = element;
        linkMode.value = true;
        paperContainer.value.style.cursor = 'crosshair';
        contextMenu.visible = false;
        element.highlight();
    }

    const deleteElement = (element) => {
        element.remove();
        contextMenu.visible = false;
        deselectElement();
    }

    const selectElement = (element, type) => {
        const dataOfEl = element.get('data');

        const currentVals = Object.assign({
            title: element.attr('label/text') || '',
            shape: 'rectangle',
            nodeType: '',
            color: element.attr('body/fill') || element.attr('line/stroke') || '#3498db',
            size: 'small',
            thickness: 'thin',
            critical: false,
            markdown: '',

            start: -1, // try with 0

        }, dataOfEl ? {
            title: dataOfEl.title,
            shape: dataOfEl.shape,
            nodeType: dataOfEl.nodeType,
            color: dataOfEl.color,
            size: dataOfEl.size,
            thickness: dataOfEl.thickness,
            critical: dataOfEl.critical,
            markdown: dataOfEl.markdown,
            costs: dataOfEl.costs,
            start: dataOfEl.start,
            duration: dataOfEl.duration
        }: null);

        if (type === 'link') {
            if (dataOfEl) {
                currentVals.costs = dataOfEl.costs;
                currentVals.duration = dataOfEl.duration;
            } else {
                currentVals.costs = -1; // try with 0
                currentVals.duration = -1; // try with 0
            }
        }

        // console.log('\x1b[32m DEBUG dev \x1b[0m', 'in selectElement', 'current data', currentVals, 'type', type);

        selectedElement.value = Object.assign({
            element,
            type
        }, currentVals);

      showProperties.value = true;
      initMarkdownEditor();
    }

    const deselectElement = () => {
      selectedElement.value = null;
      showProperties.value = false;
      if (editorView) {
        editorView.destroy();
        editorView = null;
      }
    }

    // Markdown editor
    const initMarkdownEditor = async () => {
        if (!isEditMode.value || !markdownEditor.value) {
            return;
        }

        await nextTick();

        if (editorView) {
            editorView.destroy();
        }

        editorView = new EditorView({
            doc: selectedElement.value?.markdown || '',
            extensions: [
                basicSetup,
                markdown(),
                EditorView.updateListener.of((update) => {
                    if (update.docChanged && selectedElement.value) {
                        selectedElement.value.markdown = update.state.doc.toString();
                        updateElement();
                    }
                })
            ],
            parent: markdownEditor.value
        });

        // File drop support
        markdownEditor.value.addEventListener('dragover', e => e.preventDefault());
        markdownEditor.value.addEventListener('drop', onFileDropToEditor);
    }

    // File drop to editor
    const onFileDropToEditor = (event) => {
        event.preventDefault();
        const files = Array.from(event.dataTransfer.files);

        files.forEach(file => {
            const uuid = generateUUID();
            const fileName = `${uuid}_${file.name}`;

            // Store file locally (simulate)
            const fileURL = URL.createObjectURL(file);
            localStorage.setItem(`file_${uuid}`, fileURL);

            // Insert markdown link at cursor
            const linkText = `[${file.name}](local://file_${uuid})`;
            if (editorView) {
                const cursor = editorView.state.selection.main.head;
                editorView.dispatch({
                    changes: { from: cursor, insert: linkText }
                });
            }
        });
    };

    // Update element
    const updateElement = () => {
        if (!selectedElement.value) {
            return;
        }

        const { element, title, shape, color, size, thickness, critical, costs, start, duration, markdown, nodeType } = selectedElement.value;

        if (selectedElement.value.type === 'node') {
            const fit = getLabelFit({ shape, size, title });
            if (fit) {
                // warnings will appear from getDefaultSizeOfShape
                element.resize(fit.width, fit.height);
                element.attr('body/fill', color);
                element.attr('label/text', fit.text);
                // Kein textWrap: der Text ist bereits passend umbrochen und
                // darf von JointJS nicht erneut umbrochen werden
                element.removeAttr('label/textWrap');
            }
        } else if (selectedElement.value.type === 'link') {
            console.log('\x1b[32m DEBUG dev \x1b[0m', 'update element crit', critical, element.attr('line/stroke'));
            if (critical) {
                element.attr('line/stroke', warnColor);
                element.attr('line/targetMarker/fill', warnColor);
            } else {
                element.attr('line/stroke', color);
                element.attr('line/targetMarker/fill', color);
            }
            element.attr('line/strokeWidth', thickness === 'thick' ? 4 : 2);

            // handle Link-Label
            element.removeLabel();

            if (title && title.trim()) {
                element.appendLabel({
                attrs: {
                    text: {
                        text: title,
                        fontSize: 11,
                        fill: '#333',
                        fontWeight: 'bold'
                    },
                    rect: {
                        fill: 'white',
                        stroke: color,
                        strokeWidth: 1,
                        rx: 3,
                        ry: 3
                    }
                }
                });
            }
        }

        element.set('data', {
            title, 
            nodeType, 
            shape, 
            color, 
            size, 
            thickness, 
            critical,
            markdown, 
            costs,
            duration,
            start,
            type: selectedElement.value.type,
            costs: Number(costs)
        });
    }

    // Zoom functions
    const setZoom = (level) => {
        zoomLevel.value = level;
        paper.scale(level);

        paper.translate(0, 0);
        updateElementVisibility();
    }

    const zoomToFit = () => {
        paper.scaleContentToFit({ padding: 20 });
        const scale = paper.scale();
        zoomLevel.value = scale.sx;
        updateElementVisibility();
    }

    const updateElementVisibility = () => {
        const cells = graph.getCells();

        cells.forEach(cell => {
            try {
                if (cell.isElement()) {
                    const data = cell.get('data') || {};
                    const shouldShowLabel = zoomLevel.value >= 0.7 || data.size === 'large';

                    cell.attr('label/display', shouldShowLabel ? 'block' : 'none');

                    if (zoomLevel.value < 0.7) {
                        const originalColor = data.color || '#3498db';
                        cell.attr('body/fill', fadeColor(originalColor, 0.6));
                    } else {
                        cell.attr('body/fill', data.color || '#3498db');
                    }
                } else if (cell.isLink()) {
                    const data = cell.get('data') || {};
                    const strokeWidth = zoomLevel.value < 0.7 ? 1 : (data.thickness === 'thick' ? 4 : 2);
                    cell.attr('line/strokeWidth', strokeWidth);

                    const labels = cell.labels();
                    if (labels && labels.length > 0) {
                        const shouldShowLabel = zoomLevel.value >= 0.7;
                        cell.attr('labels/0/attrs/text/display', shouldShowLabel ? 'block' : 'none');
                    }
                }
            } catch (error) {
                console.error('Error updating element visibility:', error);
            }
        })
    }

    // Utility functions
    const fadeColor = (color, opacity) => {
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        })
    }

    // History management
    const saveToHistory = () => {
        // yeah!! good luck with debugging
        if (!graph) return

        const graphJSON = graph.toJSON();
        history.splice(historyIndex.value + 1);
        history.push(JSON.stringify(graphJSON));

        if (history.length > 10) {
            history.shift();
        } else {
            historyIndex.value++;
        }

        canUndo.value = historyIndex.value > 0;
        canRedo.value = historyIndex.value < history.length - 1;
    }

    const undo = () => {
        if (historyIndex.value > 0) {
            historyIndex.value--
            const graphJSON = JSON.parse(history[historyIndex.value])
            clearMultiSelection()
            graph.fromJSON(graphJSON, { cellNamespace: joint.shapes }) // Korrekt
            canUndo.value = historyIndex.value > 0
            canRedo.value = true
        }
    }

    const redo = () => {
        if (historyIndex.value < history.length - 1) {
            historyIndex.value++
            const graphJSON = JSON.parse(history[historyIndex.value])
            clearMultiSelection()
            graph.fromJSON(graphJSON, { cellNamespace: joint.shapes }) // Korrekt
            canUndo.value = true
            canRedo.value = historyIndex.value < history.length - 1
        }
    }

    // Auto-save
    const scheduleAutoSave = () => {
        if (autoSaveTimer) clearTimeout(autoSaveTimer)
        autoSaveTimer = setTimeout(() => {
            saveNow()
        }, 30000) // 30 seconds
    }

    // Sofort ins LocalStorage speichern (z.B. vor dem Verlassen der Seite)
    const saveNow = () => {
        if (autoSaveTimer) clearTimeout(autoSaveTimer)
        if (!graph) return
        const graphData = graph.toJSON()
        localStorage.setItem('graph_autosave', JSON.stringify(graphData))
        hasUnsavedChanges.value = false
    }

    // Import/Export
    const exportGraph = () => {
      const graphData = graph.toJSON()
      const dataStr = JSON.stringify(graphData, null, 2)
      const dataBlob = new Blob([dataStr], { type: 'application/json' })
      
      const link = document.createElement('a')
      link.href = URL.createObjectURL(dataBlob)
      link.download = 'graph.json'
      link.click()
    }

    const importGraph = (event) => {
      const file = event.target.files[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const graphData = fitGraphJSONToLabels(JSON.parse(e.target.result))
          clearMultiSelection()
          graph.fromJSON(graphData, { cellNamespace: joint.shapes })
          deselectElement()
        } catch (error) {
          console.error('Import error:', error)
          alert('Fehler beim Importieren der Datei')
        }
      }
      reader.readAsText(file)
    }

    // Mode toggle
    const toggleMode = () => {
        isEditMode.value = !isEditMode.value
        paper.setInteractivity(isEditMode.value)

        if (!isEditMode.value) {
            showLibrary.value = false
            cancelLinkMode()
            clearMultiSelection()
            contextMenu.visible = false
            if (editorView) {
                editorView.destroy()
                editorView = null
            }
        } else if (selectedElement.value) {
            nextTick(() => initMarkdownEditor())
        }
    }

    // Panel resizing
    const startResize = (panel, event) => {
        const startX = event.clientX;
        const startWidth = panel === 'library' ? libraryWidth.value : propertyWidth.value;

        const onMouseMove = (e) => {
            const diff = e.clientX - startX;
            const newWidth = panel === 'library' ? startWidth + diff : startWidth - diff;

            if ((newWidth >= 200) && (newWidth <= 400)) {
                if (panel === 'library') {
                    libraryWidth.value = newWidth;
                } else {
                    propertyWidth.value = newWidth;
                }
            }
      }

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    }

    const closeProperties = () => {
      deselectElement();
    };

    // Computed
    const renderedMarkdown = ref('');
    watch(() => selectedElement.value?.markdown, (newMarkdown) => {
      if (newMarkdown && !isEditMode.value) {
        renderedMarkdown.value = marked(newMarkdown);
      }
    }, { immediate: true });

    // Lifecycle
    onMounted(async () => {
      try {
        await nextTick();
        initGraph();
        document.addEventListener('keydown', onKeyDown);

        if (props.visible) {
          // Kleiner Aufschub, damit das Layout steht
          setTimeout(loadInitialGraph, 100);
        }
        // sonst: der visible-Watcher lädt, sobald der Editor sichtbar wird

      } catch (error) {
        console.error('Error initializing graph:', error);
      }
    });

    // Der Graph darf erst geladen/gerendert werden, wenn der Editor sichtbar
    // ist: Bei display:none (v-show, z.B. Start auf der Startseite) liefert
    // getBBox() für die Anker-Magnete falsche Werte und die Kanten werden
    // gegen kaputte Geometrie geroutet ("durcheinandergewürfelte" Kanten).
    let initialGraphLoaded = false;

    function loadInitialGraph() {
      if (initialGraphLoaded || !graph) {
        return;
      }
      initialGraphLoaded = true;

      let loaded = false;
      const autosave = localStorage.getItem('graph_autosave');
      if (autosave) {
        try {
          const parsed = JSON.parse(autosave);
          // Ein leerer Autosave (z.B. nach "Zeichenfläche leeren") zählt nicht
          // als gespeicherter Stand – dann lieber das Beispiel zeigen
          if (Array.isArray(parsed.cells) && parsed.cells.length > 0) {
            const graphData = fitGraphJSONToLabels(parsed);
            graph.fromJSON(graphData, { cellNamespace: joint.shapes });
            loaded = true;
          }
        } catch (e) {
          console.warn('Could not load autosave:', e);
          localStorage.removeItem('graph_autosave');
        }
      }
      if (!loaded) {
        // Ohne gespeicherten Stand die Beispiel-Datenlandkarte zeigen
        const example = fitGraphJSONToLabels(structuredClone(exampleGraph));
        graph.fromJSON(example, { cellNamespace: joint.shapes });
      }

      // Initial history entry
      saveToHistory();

      // Das Laden von Autosave/Beispiel ist keine ungespeicherte Änderung
      hasUnsavedChanges.value = false;
    }

    watch(() => props.visible, (visible) => {
      if (visible) {
        // erst nach dem DOM-Update laden, wenn display:none aufgehoben ist
        nextTick(loadInitialGraph);
      }
    });

    onUnmounted(() => {
      document.removeEventListener('keydown', onKeyDown);
    });

    return {
      // Refs
      graphContainer,
      paperContainer,
      markdownEditor,
      fileInput,
      
      // State
      isEditMode,
      showLibrary,
      showProperties,
      zoomLevel,
      libraryWidth,
      propertyWidth,
      selectedElement,
      multiSelectedCount,
      deleteMultiSelection,
      canUndo,
      canRedo,
      templates,
      renderedMarkdown,
      
      // Link-spezifische States
      linkMode,
      contextMenu,

      // Methods
      onTemplateDragStart,
      setZoom,
      zoomToFit,
      undo,
      redo,
      exportGraph,
      importGraph,
      toggleMode,
      startResize,
      closeProperties,
      updateElement,
      
      // Link-spezifische Methods
      toggleLinkMode,
      startLinkFromElement,
      deleteElement,

      // Speichern vor Seitenwechsel (genutzt von App.vue)
      hasUnsavedChanges,
      saveNow
    };
  }
}
</script>

<style scoped>
.graph-editor {
  display: flex;
  height: 100%;
  background: #f5f5f5;
}

.library-panel, .property-panel {
  background: white;
  border-right: 1px solid #ddd;
  display: flex;
  flex-direction: column;
  position: relative;
}

.property-panel {
  border-right: none;
  border-left: 1px solid #ddd;
}

.panel-header {
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fafafa;
}

.panel-header h3 {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
}

.close-btn:hover {
  background: #eee;
  border-radius: 3px;
}

.template-grid {
  padding: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 12px;
}

.template-item {
  padding: 12px;
  border-radius: 6px;
  cursor: grab;
  text-align: center;
  border: 1px solid rgba(0,0,0,0.1);
  transition: transform 0.2s;
}

.template-item:hover {
  transform: translateY(-2px);
}

.template-item:active {
  cursor: grabbing;
}

.template-shape {
  font-size: 11px;
  font-weight: 500;
  color: #333;
}

.graph-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: white;
}

.toolbar {
  padding: 8px 16px;
  background: #fafafa;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.toolbar-left, .toolbar-right {
  display: flex;
  gap: 8px;
  align-items: center;
}

.btn {
  padding: 6px 12px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: background 0.2s;
}

.btn:hover:not(:disabled) {
  background: #f0f0f0;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn.active {
  background: #3498db;
  color: white;
  border-color: #3498db;
}

.link-btn.active {
  background: #e74c3c !important;
  color: white !important;
  border-color: #c0392b !important;
}

.divider {
  width: 1px;
  height: 20px;
  background: #ddd;
  margin: 0 8px;
}

.paper-container {
  flex: 1;
  position: relative;
  overflow: hidden;
}

.paper-container.link-mode {
  cursor: crosshair !important;
}

.selection-lasso {
  position: absolute;
  border: 1px dashed #0d6efd;
  background: rgba(13, 110, 253, 0.08);
  pointer-events: none;
  z-index: 50;
}

.btn.btn-danger {
  background: #dc3545;
  color: white;
  border-color: #dc3545;
}

.btn.btn-danger:hover {
  background: #bb2d3b;
}

.property-content {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 16px;
}

.form-group.checkbox-group {
    input[type="checkbox"] {
        display: inline-block;
        margin-left: 5px;
        width: 20px;
        position: relative;
        top: 3px;
    }
}

.form-group label {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #333;
}

.form-input, .form-select {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
}

.form-color {
  width: 100%;
  height: 36px;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
}

.markdown-editor {
  border: 1px solid #ddd;
  border-radius: 4px;
  min-height: 200px;
}

.markdown-preview {
  font-size: 14px;
  line-height: 1.6;
}

.view-single-property {
    font-size: 12px;
    line-height: 1.6;
    margin: 10px 0;
}

.resize-handle-right, .resize-handle-left {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 4px;
  cursor: col-resize;
  background: transparent;
}

.resize-handle-right {
  right: -2px;
}

.resize-handle-left {
  left: -2px;
}

.resize-handle-right:hover, .resize-handle-left:hover {
  background: #3498db;
}

.context-menu {
  position: fixed;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 1000;
  min-width: 160px;
  overflow: hidden;
}

.context-item {
  display: block;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  font-size: 13px;
  color: #333;
  transition: background 0.2s;
}

.context-item:hover {
  background: #f8f9fa;
}

.context-item:first-child {
  border-bottom: 1px solid #eee;
}

.mode-toggle {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
}

.mode-btn {
  width: 48px;
  height: 48px;
  border: 2px solid #ddd;
  background: white;
  border-radius: 50%;
  cursor: pointer;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: all 0.2s;
}

.mode-btn:hover {
  transform: scale(1.05);
}

.mode-btn.locked {
  background: #e74c3c;
  border-color: #c0392b;
  color: white;
}

/* JointJS-spezifische Styles */
:deep(svg) {
  width: 100% !important;
  height: 100% !important;
}

:deep(.joint-paper) {
  width: 100% !important;
  height: 100% !important;
}

:deep(.joint-paper-grid) {
  opacity: 0.3;
}

:deep(.joint-element) {
  cursor: pointer;
}

:deep(.joint-element:hover) {
  filter: brightness(1.1);
}

/* :deep(.joint-element text) {
    textLength: 40px    ;
} */

/* defines the appearance of potential aims of an edge */
:deep(.available-magnet) {
  fill: #93ccf1 !important;
  stroke: #ffefef !important;
  stroke-width: 1 !important;
}

:deep(.joint-element.joint-element-highlighted) {
  filter: drop-shadow(0 0 8px #3498db);
}

:deep(.joint-link) {
  cursor: pointer;
}

:deep(.joint-link:hover .connection) {
  stroke: #2980b9 !important;
  stroke-width: 3 !important;
}

:deep(.joint-element.magnet-availability) {
  cursor: crosshair;
}

/* CodeMirror overrides */
:deep(.cm-editor) {
  border: none;
  font-size: 13px;
}

:deep(.cm-content) {
  padding: 12px;
}
</style>